/**
 * Overseer advisory text for AI usage & capacity.
 *
 * Advise; do not interrupt. Never invent provider numbers. Explicit pins must
 * never be described as silently substitutable.
 */

import type { AiCapacityMeter } from './meters.js';
import type { AiUsagePolicy } from './limits.js';
import { DEFAULT_AI_USAGE_POLICY } from './limits.js';

export interface UsageAdvisory {
  readonly severity: 'info' | 'attention' | 'urgent';
  readonly text: string;
  readonly meterKey: string | null;
}

function providerLabel(meter: AiCapacityMeter): string {
  return meter.title || meter.provider || meter.runtime || 'A provider';
}

/**
 * Turn meter snapshots + policy into advisory strings for Owner Attention /
 * Overseer copy. Pure: no I/O, no permission prompts.
 */
export function adviseAiUsage(
  meters: readonly AiCapacityMeter[],
  policy: AiUsagePolicy = DEFAULT_AI_USAGE_POLICY,
): readonly UsageAdvisory[] {
  const out: UsageAdvisory[] = [];

  for (const meter of meters) {
    if (meter.quotaSignal === 'exhausted') {
      const reset = meter.resetsLabel ? ` ${capitalize(meter.resetsLabel)}.` : '';
      out.push({
        severity: 'urgent',
        meterKey: meter.key,
        text: policy.exhaustionBehavior === 'advise_only'
          || policy.neverSubstitutePinned
          ? `${providerLabel(meter)} is exhausted until reset.${reset} I’ll avoid routing new tasks there unless you override.`
          : `${providerLabel(meter)} is exhausted until reset.${reset} Routing will follow your fallback policy; pinned offerings still will not be silently substituted.`,
      });
      continue;
    }
    if (meter.quotaSignal === 'signed_out') {
      out.push({
        severity: 'urgent',
        meterKey: meter.key,
        text: `${providerLabel(meter)} is signed out. Sign in on that computer before expecting work there.`,
      });
      continue;
    }
    if (meter.band === 'hard_exceeded') {
      out.push({
        severity: 'urgent',
        meterKey: meter.key,
        text: `${providerLabel(meter)} is at or past your hard usage limit${meter.usedPercent === null ? '' : ` (${Math.round(meter.usedPercent)}% used)`}. Pause that lane or raise the limit before spending more.`,
      });
      continue;
    }
    if (meter.band === 'reserve_breached') {
      out.push({
        severity: 'attention',
        meterKey: meter.key,
        text: `${providerLabel(meter)} has entered the reserve you set aside for high-priority work. Prefer other providers for ordinary tasks.`,
      });
      continue;
    }
    if (meter.band === 'soft_exceeded' || meter.band === 'warn') {
      const pct = meter.usedPercent === null ? null : Math.round(meter.usedPercent);
      out.push({
        severity: 'attention',
        meterKey: meter.key,
        text: pct === null
          ? `${providerLabel(meter)} is near a usage warning threshold. Consider shifting ordinary review tasks elsewhere.`
          : `${providerLabel(meter)} is near its limit (${pct}% used). I recommend shifting ordinary review tasks to another provider when one is available.`,
      });
      continue;
    }
    if (meter.quotaSignal === 'rate_limited') {
      out.push({
        severity: 'attention',
        meterKey: meter.key,
        text: `${providerLabel(meter)} is rate limited. New work there will wait or move according to your routing policy.`,
      });
    }
  }

  if (policy.neverSubstitutePinned) {
    // One standing reminder when any meter needs attention — pin safety is policy, not noise.
    const needsAttention = out.some((item) => item.severity !== 'info');
    if (needsAttention) {
      out.push({
        severity: 'info',
        meterKey: null,
        text: 'Explicit model pins stay in force: Brunel will not silently substitute a pinned offering.',
      });
    }
  }

  if (out.length === 0 && meters.some((meter) => meter.known && meter.hosting === 'local')) {
    const localCount = meters.filter((meter) => meter.hosting === 'local' && meter.known).length;
    out.push({
      severity: 'info',
      meterKey: null,
      text: `Local runtimes are available on ${localCount} meter${localCount === 1 ? '' : 's'} — prefer them for low-risk work when you want to save cloud allowance.`,
    });
  }

  return out;
}

function capitalize(text: string): string {
  if (text.length === 0) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
